import { Refresh } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DialogSelect from 'components/Dialog/DialogSelect.js';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
import DropdownMenuVertical from 'components/DropdownMenuVertical';
import MDBox from 'components/MDBox';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DashboardLayout from 'layouts/LayoutContainers/DashboardLayout';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { setControllerTimeRangeValue } from 'stores/sensor/controllersSlice';
import { TimeInMS } from 'types/time';
import { fetchUsersDetails } from '../../../stores/sensor/usersSlice';
import { fetchDeviceSetPointHistory } from '../../../stores/adminCenter/adminCenterSlice';
import { DASHBOARD_CONFIG_FIELD_LABELS, DASHBOARD_CONFIG_VALUE_LABELS } from './dashboardConfigFieldLabels';

const TimeRangeOptions = [
  { label: '1 Hour', value: TimeInMS.Hour },
  { label: 'Today', value: TimeInMS.Day },
  { label: '1 Week', value: TimeInMS.Week },
  { label: '1 Month', value: TimeInMS.Month },
  { label: 'date to date', value: 'date to date' },
];

const topBarIconSx = {
  refresh: { fontSize: '1.5rem' },
};

const BINARY_ON_OFF_FIELDS = new Set(['on_off', 'channel_1', 'channel_2']);

const LOG_CATEGORY_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'device_set_point', label: 'Device set points' },
  { value: 'device_edit', label: 'Device edits' },
  { value: 'solin_updater', label: 'Solin updater' },
  { value: 'network_reboot', label: 'Network reboots' },
];

const parseAuditTimestamp = timestamp => {
  if (!timestamp) {
    return null;
  }

  if (typeof timestamp !== 'string') {
    return new Date(timestamp);
  }

  return new Date(timestamp.replace(/Z$/i, ''));
};

const formatTimestamp = timestamp => {
  if (!timestamp) {
    return null;
  }

  const parsedTimestamp = parseAuditTimestamp(timestamp);
  if (!parsedTimestamp || Number.isNaN(parsedTimestamp.getTime())) {
    return null;
  }

  const hours = String(parsedTimestamp.getHours()).padStart(2, '0');
  const minutes = String(parsedTimestamp.getMinutes()).padStart(2, '0');
  const seconds = String(parsedTimestamp.getSeconds()).padStart(2, '0');
  const day = parsedTimestamp.getDate();
  const month = parsedTimestamp.getMonth() + 1;
  const year = String(parsedTimestamp.getFullYear()).slice(-2);

  return `${hours}:${minutes}:${seconds}, ${day}/${month}/${year}`;
};

const formatStateValue = stateValue => {
  if (!stateValue) {
    return '-';
  }

  if (typeof stateValue === 'string') {
    return stateValue;
  }

  const formatFieldValue = (key, value) => {
    if (BINARY_ON_OFF_FIELDS.has(key)) {
      if (value === 0 || value === '0') return 'OFF';
      if (value === 1 || value === '1') return 'ON';
    }

    const valueLabels = DASHBOARD_CONFIG_VALUE_LABELS[key];

    if (!valueLabels) {
      return value;
    }

    return valueLabels[value] || valueLabels[String(value)] || value;
  };

  const formattedEntries = Object.entries(stateValue).map(([key, value]) => ({
    key,
    label: DASHBOARD_CONFIG_FIELD_LABELS[key] || key,
    value: formatFieldValue(key, value),
  }));
  const channelEntries = formattedEntries.filter(({ key }) => key === 'channel_1' || key === 'channel_2');
  const nonChannelEntries = formattedEntries.filter(({ key }) => key !== 'channel_1' && key !== 'channel_2');
  const formattedStateParts = nonChannelEntries.map(({ label, value }) => `${label}: ${value}`);

  if (channelEntries.length > 0) {
    formattedStateParts.push(`(${channelEntries.map(({ label, value }) => `${label}: ${value}`).join(', ')})`);
  }

  return formattedStateParts.join(', ');
};

export const DeviceSetPointHistory = () => {
  const dispatch = useDispatch();
  const users = useSelector(state => state.users.users || []);
  const usersLoading = useSelector(state => state.users.loading);
  const rangeObj = useSelector(state => state.controllers.timeRange);
  const selectedFilterObject = useSelector(state => state.persistence.selectedFilterObject || {});
  const { deviceSetPointHistory, deviceSetPointHistoryLoading, deviceSetPointHistoryError } = useSelector(
    state => state.adminCenter,
  );
  const [selectedOperator, setSelectedOperator] = useState('all');
  const [selectedLogCategory, setSelectedLogCategory] = useState('all');
  const [timeSortDirection, setTimeSortDirection] = useState('desc');
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { distributor, customer, building, floor, area, searchString } = selectedFilterObject;

  useEffect(() => {
    if (users.length === 0 && !usersLoading) {
      dispatch(fetchUsersDetails());
    }
  }, [dispatch, users.length, usersLoading]);

  const operatorOptions = useMemo(
    () => [
      { value: 'all', label: 'All Operators' },
      ...users.map(user => ({
        value: String(user.id || user.user_id),
        label: `${user.full_name} (${user.username})` || `User ${user.id || user.user_id}`,
      })),
    ],
    [users],
  );

  const operatorsByIdOrUsername = useMemo(() => {
    const operatorsMap = new Map();

    users.forEach(user => {
      const userId = user.id || user.user_id;
      if (userId) {
        operatorsMap.set(`id:${String(userId)}`, user);
      }
      if (user.username) {
        operatorsMap.set(`username:${user.username}`, user);
      }
      if (user.user_name) {
        operatorsMap.set(`username:${user.user_name}`, user);
      }
    });

    return operatorsMap;
  }, [users]);

  const formatHistoryUserName = useCallback(
    historyRow => {
      const operator =
        operatorsByIdOrUsername.get(`id:${String(historyRow.user_id)}`) ||
        operatorsByIdOrUsername.get(`username:${historyRow.user_name}`);
      const userName = historyRow.user_name || operator?.username || operator?.user_name;
      const fullName = operator?.full_name;

      if (userName && fullName) {
        return `${userName} (${fullName})`;
      }

      return userName || fullName || '-';
    },
    [operatorsByIdOrUsername],
  );

  const getActiveHistoryFilters = useCallback(
    (timeRange = rangeObj) => {
      const normalizeFilter = value => {
        if (value === null || value === undefined || value === '') return 'all';
        return value;
      };

      return {
        distributor: normalizeFilter(distributor),
        customer: normalizeFilter(customer),
        building: normalizeFilter(building),
        floor: normalizeFilter(floor),
        area: normalizeFilter(area),
        operator: normalizeFilter(selectedOperator),
        timeRange: timeRange?.value || TimeInMS.Day,
        userTimeZone,
      };
    },
    [area, building, customer, distributor, floor, rangeObj, selectedOperator, userTimeZone],
  );

  const refreshDeviceSetPointHistory = useCallback(
    (timeRange = rangeObj) => {
      dispatch(fetchDeviceSetPointHistory(getActiveHistoryFilters(timeRange)));
    },
    [dispatch, getActiveHistoryFilters, rangeObj],
  );

  useEffect(() => {
    refreshDeviceSetPointHistory();
  }, [refreshDeviceSetPointHistory]);

  const changeTimeRange = range => {
    dispatch(setControllerTimeRangeValue(range));
  };

  const refreshLastUpdateAndLastUpdateTime = () => {
    refreshDeviceSetPointHistory();
  };

  const sortedDeviceSetPointHistory = useMemo(() => {
    const sortMultiplier = timeSortDirection === 'asc' ? 1 : -1;
    const normalizedSearchString = String(searchString || '')
      .trim()
      .toLowerCase();

    return deviceSetPointHistory
      .filter(historyRow => {
        if (selectedLogCategory !== 'all' && historyRow.log_category !== selectedLogCategory) {
          return false;
        }

        if (!normalizedSearchString) {
          return true;
        }

        return String(historyRow.device_name || '')
          .toLowerCase()
          .includes(normalizedSearchString);
      })
      .sort((firstRow, secondRow) => {
        const firstTime = firstRow.timestamp ? parseAuditTimestamp(firstRow.timestamp).getTime() : 0;
        const secondTime = secondRow.timestamp ? parseAuditTimestamp(secondRow.timestamp).getTime() : 0;

        return (firstTime - secondTime) * sortMultiplier;
      });
  }, [deviceSetPointHistory, searchString, selectedLogCategory, timeSortDirection]);

  const handleTimeSortClick = () => {
    setTimeSortDirection(currentDirection => (currentDirection === 'asc' ? 'desc' : 'asc'));
  };

  const renderTimeHeader = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span>Time</span>
      <IconButton size="small" aria-label="Sort time" onClick={handleTimeSortClick} sx={{ p: 0.25 }}>
        {timeSortDirection === 'asc' ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
      </IconButton>
    </Box>
  );

  const emptyStateText = deviceSetPointHistoryError || 'No system logs to display';

  return (
    <DashboardLayout>
      <DashboardNavbar objectName="Device Set Point History" />
      <Typography
        fontSize="11px"
        sx={{
          paddingLeft: { xs: 0, sm: '12px' },
          marginTop: { xs: '-15px', sm: '-5px' },
          textAlign: { xs: 'center', sm: 'left' },
        }}>
        Smart indoor air monitoring & management platform
      </Typography>
      <MDBox pt={{ xs: 7, sm: 1 }} pb={1}>
        <Box sx={{ mb: 2, px: { xs: 0, sm: 1.5 }, textAlign: { xs: 'center', sm: 'left' } }}>
          <Typography sx={{ color: '#152536', fontSize: 28, fontWeight: 800, lineHeight: 1.2 }}>System Logs</Typography>
          <Typography sx={{ color: '#6d7178', fontSize: 15, fontWeight: 500, mt: 0.75 }}>
            Review device changes, updater results, and network reboots for the selected devices and time range.
          </Typography>
        </Box>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <MDBox
              mb={1}
              px={2}
              variant="gradient"
              borderRadius="lg"
              coloredShadow="light"
              id="topBar"
              sx={{
                position: 'relative',
                zIndex: 1200,
              }}
              style={{
                background: '#DEE4F9',
              }}>
              <Box
                sx={{
                  display: { xs: 'flex', sm: 'grid' },
                  gridTemplateColumns: { sm: '1fr', lg: 'minmax(0, 1fr) auto' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: { xs: 1, lg: 2 },
                }}>
                <Box sx={{ gridColumn: '1 / 2', width: '100%', minWidth: 0 }}>
                  <DevicesListFilter
                    type="device"
                    status={false}
                    fullWidth
                    extraDropdownItems={[
                      <DropdownMenuVertical
                        key="operator"
                        labelName="Operator"
                        options={operatorOptions}
                        selectedOption={selectedOperator}
                        handleChange={event => setSelectedOperator(event.target.value)}
                        disabled={usersLoading}
                        width="100%"
                      />,
                      <DropdownMenuVertical
                        key="log-type"
                        labelName="Log type"
                        options={LOG_CATEGORY_OPTIONS}
                        selectedOption={selectedLogCategory}
                        handleChange={event => setSelectedLogCategory(event.target.value)}
                        width="100%"
                      />,
                    ]}
                  />
                </Box>

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                    justifyContent: { xs: 'center', lg: 'flex-end' },
                    gridColumn: { xs: '1 / -1', lg: '2 / 3' },
                    width: { xs: '100%', lg: 'auto' },
                    flexShrink: 0,
                  }}>
                  <DialogSelect
                    buttonTitle="Time Range"
                    rangeObj={rangeObj}
                    dialogTitle="Choose Time Range"
                    color="#000"
                    onSelectionChanged={changeTimeRange}
                    onSelectionOK={setControllerTimeRangeValue}
                    selectLabel="Time Range"
                    selectOptions={TimeRangeOptions}
                    default={TimeRangeOptions[1]}
                    onClose={refreshLastUpdateAndLastUpdateTime}
                  />
                  <IconButton sx={{ color: '#000' }} onClick={() => refreshLastUpdateAndLastUpdateTime()}>
                    <Refresh sx={topBarIconSx.refresh} />
                  </IconButton>
                </Stack>
              </Box>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Box sx={{ mt: 1 }}>
        <TableContainer
          component={Paper}
          sx={{
            height: {
              xs: 'calc(100dvh - 330px)',
              sm: 'calc(100dvh - 285px)',
            },
            minHeight: 260,
          }}>
          <Table
            stickyHeader
            size="small"
            aria-label="Device set point history"
            sx={{
              minWidth: 950,
              tableLayout: 'fixed',
            }}>
            <colgroup>
              <col style={{ width: '18%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '23%' }} />
              <col style={{ width: '23%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>{renderTimeHeader()}</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Previous state</TableCell>
                <TableCell>New state</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deviceSetPointHistoryLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text">
                      Loading device set point history...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : sortedDeviceSetPointHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color={deviceSetPointHistoryError ? 'error' : 'text'}>
                      {emptyStateText}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedDeviceSetPointHistory.map((historyRow, index) => (
                  <TableRow key={historyRow.id || index} hover>
                    <TableCell>{formatTimestamp(historyRow.timestamp) || '-'}</TableCell>
                    <TableCell>{formatHistoryUserName(historyRow)}</TableCell>
                    <TableCell>{historyRow.device_name || '-'}</TableCell>
                    <TableCell>{formatStateValue(historyRow.previous_state)}</TableCell>
                    <TableCell>{historyRow.event_type || formatStateValue(historyRow.new_state)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </DashboardLayout>
  );
};

export default DeviceSetPointHistory;
